/*@flow*/
import React, { createElement } from 'react';
import cx from 'classnames';
import Alert from './Alert';
import Fieldset from './Fieldset';
import type { LocalsT, ListLocals, Item, ButtonT } from '../types';

function getBreakpoints(sizeMap) {
  const className = {};
  Object.keys(sizeMap).forEach(size => {
    let prefix = 'col-';
    if (size !== 'xs') {
      prefix = 'col-' + size + '-';
    }
    className[prefix + sizeMap[size]] = true;
  });
  return className;
}

type PropsT = {
  ...$Exact<ListLocals>,
  ...$Exact<LocalsT>,
};

type Registry = {
  Help: React$StatelessFunctionalComponent<*>,
  RowWithoutButtons: React$StatelessFunctionalComponent<*>,
  RowButton: React$StatelessFunctionalComponent<*>,
  ButtonGroup: React$StatelessFunctionalComponent<*>,
  Row: React$StatelessFunctionalComponent<*>,
  AddButton: React$StatelessFunctionalComponent<*>,
  Fieldset: React$StatelessFunctionalComponent<*>,
};

const clone = (overrides: $Shape<Registry> = {}) => {
  let registry = {};

  function Column({
    sizeMap,
    children,
  }: {
    sizeMap: { [key: string]: number },
    children: React$Node,
  }) {
    const bkpts = getBreakpoints(sizeMap);
    const className = cx(bkpts);
    return <div className={className}>{children}</div>;
  }

  function Help(locals: PropsT) {
    return createElement(Alert, { type: 'info', message: locals.help });
  }

  function ErrorComponent(locals: PropsT) {
    return createElement(Alert, { type: 'danger', message: locals.error });
  }

  function RowWithoutButtons(props: Item) {
    return (
      <div data-testid="RowWithoutButtons" className="row">
        <Column sizeMap={{ xs: 12 }}>{props.input}</Column>
      </div>
    );
  }

  function RowButton(props: ButtonT) {
    return (
      <button
        key={props.type}
        type="button"
        className={`btn btn-secondary btn-${props.type}`}
        onClick={props.click}
      >
        {props.label}
      </button>
    );
  }

  function ButtonGroup(props: { children: ButtonT[] }) {
    return (
      <div className="btn-group">
        {props.children.map((v, i) => (
          <registry.RowButton {...v} key={i} />
        ))}
      </div>
    );
  }

  function Row({ row }) {
    return (
      <div className="form-row">
        <Column sizeMap={{ sm: 8, xs: 6 }}>{row.input}</Column>
        <Column sizeMap={{ sm: 4, xs: 6 }}>
          <registry.ButtonGroup>{row.buttons}</registry.ButtonGroup>
        </Column>
      </div>
    );
  }

  function AddButton(locals: PropsT) {
    const button = locals.add;
    if (!button) return null;
    return (
      <div>
        <div className="mb-3">
          <button
            type="button"
            className={`btn btn-secondary btn-${button.type}`}
            onClick={button.click}
          >
            {button.label}
          </button>
        </div>
      </div>
    );
  }

  function List(locals: PropsT) {
    const { items } = locals;
    let rows =
      items &&
      items.map((item, i) => {
        if (item.buttons.length === 0) {
          return <registry.RowWithoutButtons {...item} key={i * 10} />;
        }
        return <registry.Row {...locals} row={item} key={i * 10} />;
      });

    return (
      <registry.Fieldset {...locals} rows={rows}>
        {locals.error && locals.hasError && <ErrorComponent {...locals} />}
        {rows}
        {locals.add && <registry.AddButton {...locals} />}
        {locals.help && <registry.Help {...locals} />}
      </registry.Fieldset>
    );
  }

  List.clone = clone;

  registry = {
    Help,
    RowWithoutButtons,
    RowButton,
    ButtonGroup,
    Row,
    AddButton,
    Fieldset,
    ...overrides,
  };

  return List;
};

export default clone();
